import { useContext, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectSystem, selectUser } from "module/auth/redux/authSlice";
import { EntityIdentifier, EntityType } from "module/entity/types";
import { canRemoveMaintenanceTaskAttachment } from "module/maintenanceTaskAttachment/helper/canRemoveMaintenanceTaskAttachment";
import MaintenanceTaskContext from "module/maintenanceTask/context/MaintenanceTaskContext";
import { MaintenanceTaskInterface } from "module/maintenanceTask/types";
import MaintenanceTaskOpenCloseContext from "module/maintenanceTask/openClose/context/MaintenanceTaskOpenCloseContext";
import {
  removeFromEntityPropertyList,
  removeFromMyEntities,
} from "module/staff/redux/staffDataSlice";

interface Result {
  canRemove: boolean;
  remove: () => void;
}

const useMaintenanceTaskAttachmentRemove = (
  entityIdentifier: EntityIdentifier
): Result => {
  const { entity } = useContext(MaintenanceTaskContext);
  const maintenanceTask = entity as MaintenanceTaskInterface;
  const maintenanceTaskEntityIdentifier = useMemo(
    () => ({ id: maintenanceTask.id, type: EntityType.maintenanceTask }),
    [maintenanceTask.id]
  );
  const { isOpen } = useContext(MaintenanceTaskOpenCloseContext);

  const dispatch = useAppDispatch();
  const staffId = useAppSelector(selectUser).id;
  const systemShortName = useAppSelector(selectSystem).shortName;

  const canRemove = useMemo(
    () =>
      canRemoveMaintenanceTaskAttachment({
        maintenanceTask,
        isMaintenanceTaskOpen: isOpen,
      }),
    [isOpen, maintenanceTask]
  );

  const remove = useCallback(() => {
    dispatch(
      removeFromEntityPropertyList({
        staffId,
        systemShortName,
        baseEntityIdentifier: maintenanceTaskEntityIdentifier,
        propertyName: "attachments",
        entityIdentifierToRemove: entityIdentifier,
      })
    );

    dispatch(
      removeFromMyEntities({
        staffId,
        systemShortName,
        entityIdentifiers: [entityIdentifier],
      })
    );
  }, [
    dispatch,
    entityIdentifier,
    maintenanceTaskEntityIdentifier,
    staffId,
    systemShortName,
  ]);

  return {
    canRemove,
    remove,
  };
};

export default useMaintenanceTaskAttachmentRemove;
